import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { LinearTranslation } from "~/Configurators/common/components/LinearTranslation";
import { getStepNameKey } from "~/Configurators/common/advancedTranslationUtils";
import { type ReactNode, useMemo } from "react";
import { getNextStep, getPreviousStep } from "~/Configurators/linearStepHelper";
import type { RootState } from "~/redux/store";
import { getCurrentStep, getSteps } from "~/Configurators/Linear/redux/selectors";
import { useSelector } from "react-redux";

export const useRenderStepName = () => {
  const xmlConfig = useXmlConfiguratorTranslations();
  const { t } = xmlConfig;

  const renderStepName: (index: number) => ReactNode = useMemo(() => {
    if (t) {
      return xmlConfig.renderStepName;
    } else {
      return (index: number) => {
        return <LinearTranslation translationKey={getStepNameKey(index + 1)} />;
      };
    }
  }, [t]);

  return renderStepName;
};

export const useNextStepName = () => {
  const renderStepName = useRenderStepName();
  const currentStepIndex = useSelector(getCurrentStep);
  const steps = useSelector(getSteps);
  let nextStepIndex = useSelector(selectNextActiveStepIndex);
  if (nextStepIndex === -1) {
    nextStepIndex = currentStepIndex + 1;
  }
  if (nextStepIndex === -1 || nextStepIndex > steps.length - 1) {
    return null;
  }
  return renderStepName(nextStepIndex);
};

export const usePreviousStepName = () => {
  const renderStepName = useRenderStepName();
  const currentStepIndex = useSelector(getCurrentStep);
  let previousStepIndex = useSelector(selectPreviousActiveStepIndex);
  if (previousStepIndex === -1) {
    previousStepIndex = currentStepIndex - 1;
  }
  if (previousStepIndex === -1) {
    return null;
  }
  return renderStepName(previousStepIndex);
};

const selectNextActiveStepIndex = (state: RootState) => {
  const steps = getSteps(state);
  const currentStepIndex = getCurrentStep(state);
  const configuratorType = state.linearConfigurator.type!;
  const nextSteps = state.linearConfigurator.nextSteps;
  const nextStep = getNextStep(configuratorType, currentStepIndex, steps, nextSteps);
  return nextStep ? steps.indexOf(nextStep) : -1;
};

const selectPreviousActiveStepIndex = (state: RootState) => {
  const steps = getSteps(state);
  const currentStepIndex = getCurrentStep(state);
  const configuratorType = state.linearConfigurator.type!;
  const previousSteps = state.linearConfigurator.previousSteps;
  const previousStep = getPreviousStep(configuratorType, currentStepIndex, steps, previousSteps);
  return previousStep ? steps.indexOf(previousStep) : -1;
};
